const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const DiffRow = ({ label, a, b, fmt = (x) => x ?? "—" }) => {
  const differs = !same(a, b);
  return (
    <div className={`grid grid-cols-[200px_1fr_1fr] gap-4 px-5 py-2.5 border-b border-[var(--o-border-soft)] text-sm ${differs ? "bg-[#F59E0B]/[0.06]" : ""}`} data-testid={`diff-row-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <span className="text-xs text-[var(--o-muted)] flex items-center gap-2">
        {differs && <span className="h-1.5 w-1.5 rounded-full bg-[#F59E0B]" />}{label}
      </span>
      <span className="font-mono text-xs break-all">{fmt(a)}</span>
      <span className="font-mono text-xs break-all">{fmt(b)}</span>
    </div>
  );
};

const PromptDiff = ({ label, a = "", b = "" }) => {
  if (a === b) return <DiffRow label={label} a="identical" b="identical" />;
  const la = a.split("\n");
  const lb = b.split("\n");
  const onlyA = la.filter((l) => l.trim() && !lb.includes(l));
  const onlyB = lb.filter((l) => l.trim() && !la.includes(l));
  return (
    <div className="grid grid-cols-[200px_1fr_1fr] gap-4 px-5 py-3 border-b border-[var(--o-border-soft)] bg-[#F59E0B]/[0.06]" data-testid={`prompt-diff-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <span className="text-xs text-[var(--o-muted)] flex items-start gap-2"><span className="h-1.5 w-1.5 mt-1.5 rounded-full bg-[#F59E0B]" />{label}</span>
      <div className="space-y-1">{onlyA.map((l, i) => <p key={i} className="font-mono text-[11px] text-[#EF4444] border-l-2 border-[#EF4444] pl-2">− {l}</p>)}{!onlyA.length && <p className="font-mono text-[11px] text-[var(--o-muted3)]">no unique lines</p>}</div>
      <div className="space-y-1">{onlyB.map((l, i) => <p key={i} className="font-mono text-[11px] text-[#10B981] border-l-2 border-[#10B981] pl-2">+ {l}</p>)}{!onlyB.length && <p className="font-mono text-[11px] text-[var(--o-muted3)]">no unique lines</p>}</div>
    </div>
  );
};

const pct = (v) => (v == null ? "—" : `${Math.round(v * 100)}%`);

export const ConfigDiff = ({ A, B }) => {
  const s = (r) => r.settings;
  const st = (r) => r.stats || {};
  return (
    <div className="border border-[var(--o-border)]" data-testid="config-diff">
      <h2 className="font-display text-base md:text-lg font-bold px-5 py-4 border-b border-[var(--o-border)]">What changed</h2>
      <DiffRow label="Recording" a={A.replay?.filename || A.mode} b={B.replay?.filename || B.mode} />
      <DiffRow label="Reasoning provider" a={s(A).provider} b={s(B).provider} />
      <DiffRow label="Anthropic model" a={A.models?.anthropic} b={B.models?.anthropic} />
      <DiffRow label="Transcription model" a={A.models?.transcription} b={B.models?.transcription} />
      <DiffRow label="Flag model (fallback)" a={A.models?.flag_fallback} b={B.models?.flag_fallback} />
      <DiffRow label="Usefulness threshold" a={s(A).usefulness_threshold} b={s(B).usefulness_threshold} />
      <DiffRow label="Flag confidence" a={s(A).flag_confidence_threshold} b={s(B).flag_confidence_threshold} />
      <DiffRow label="Flag interval (min)" a={s(A).flag_interval_min} b={s(B).flag_interval_min} />
      <DiffRow label="Chunk length (s)" a={s(A).chunk_seconds} b={s(B).chunk_seconds} />
      <PromptDiff label="System prompt" a={A.system_prompt} b={B.system_prompt} />
      <PromptDiff label="Flag prompt" a={A.flag_prompt || ""} b={B.flag_prompt || ""} />
      <h2 className="font-display text-base md:text-lg font-bold px-5 py-4 border-y border-[var(--o-border)] mt-2">Outcome</h2>
      <DiffRow label="Answers (shown)" a={`${st(A).answers ?? 0} (${st(A).answers_shown ?? 0})`} b={`${st(B).answers ?? 0} (${st(B).answers_shown ?? 0})`} />
      <DiffRow label="Median latency" a={st(A).median_answer_latency_ms} b={st(B).median_answer_latency_ms} fmt={(x) => (x == null ? "—" : `${(x / 1000).toFixed(1)}s`)} />
      <DiffRow label="Flags shown / clicked" a={`${st(A).flags_shown ?? 0} / ${st(A).flags_clicked ?? 0}`} b={`${st(B).flags_shown ?? 0} / ${st(B).flags_clicked ?? 0}`} />
      <DiffRow label="Chunk failures" a={st(A).chunk_failures} b={st(B).chunk_failures} />
      <DiffRow label="Est. cost" a={st(A).estimated_cost_usd} b={st(B).estimated_cost_usd} fmt={(x) => (x == null ? "—" : `$${x}`)} />
      <DiffRow label="Answers scored 3 by both" a={A.scoring_summary?.answers_both_3_rate} b={B.scoring_summary?.answers_both_3_rate} fmt={pct} />
      <DiffRow label="Scorer agreement" a={A.scoring_summary?.agreement} b={B.scoring_summary?.agreement} fmt={pct} />
      <DiffRow label="Verdict" a={A.scoring_summary?.verdict} b={B.scoring_summary?.verdict} />
    </div>
  );
};
